import type React from 'react'
import { applyCartAddons } from './cartAddons'

/**
 * Ad-platform tracking.
 *
 * Browser pixel: PageView on every page, ViewContent on the sales page, CustomizeProduct on
 * every product choice, AddToCart on every click that goes to Shopify, CompleteRegistration /
 * Lead / Contact on the owner forms. InitiateCheckout and Purchase come from Shopify's checkout
 * (Facebook & Instagram app, data sharing "Maximum").
 *
 * Conversions API: every event carries a unique event_id. When META_CAPI_ENDPOINT is set, the
 * same event (same event_id, fbp, fbc, hashed email, external_id) is also sent to that server,
 * which forwards it to Meta with the access token. Meta de-duplicates browser + server copies.
 * The access token must NEVER be placed in this site's code.
 */
export const META_PIXEL_ID = '1350274987135662'
export const META_CAPI_ENDPOINT = ''
export const TIKTOK_PIXEL_ID = ''

type EventName =
  | 'PageView'
  | 'ViewContent'
  | 'AddToCart'
  | 'InitiateCheckout'
  | 'Lead'
  | 'CustomizeProduct'
  | 'CompleteRegistration'
  | 'Contact'

/**
 * Custom events for the conversion system. Standard pixel events above stay
 * untouched so existing Purchase / AddToCart / InitiateCheckout reporting keeps
 * working; these ride alongside as custom events for funnel analysis.
 */
export type CustomEventName =
  | 'bundle_1_selected'
  | 'bundle_3_selected'
  | 'bundle_5_selected'
  | 'business_pack_viewed'
  | 'business_pack_clicked'
  | 'objection_popup_viewed'
  | 'objection_popup_closed'
  | 'objection_battery_selected'
  | 'objection_visibility_selected'
  | 'objection_antifog_selected'
  | 'objection_lenses_selected'
  | 'objection_durability_selected'
  | 'objection_price_selected'
  | 'objection_none_want_it'
  | 'mini_funnel_battery_viewed'
  | 'mini_funnel_visibility_viewed'
  | 'mini_funnel_antifog_viewed'
  | 'mini_funnel_lenses_viewed'
  | 'mini_funnel_durability_viewed'
  | 'mini_funnel_price_viewed'
  | 'mini_funnel_cta_clicked'
  | 'price_split_offer_viewed'
  | 'price_split_clicked'
  | 'rescue_5_clicked'
  | 'rx_insert_updates_clicked'
  | 'mystery_gift_offer_viewed'
  | 'value_stack_viewed'
  | 'add_to_cart_single'
  | 'add_to_cart_full_shift'
  | 'add_to_cart_max_value'
  | 'add_to_cart_business'
  | 'two_x1_free_third_viewed'
  | 'two_x1_free_third_accepted'
  | 'two_x1_free_third_declined'
  | 'single_lifetime_995_viewed'
  | 'single_lifetime_995_accepted'
  | 'single_lifetime_995_declined'
  | 'single_to_trio_viewed'
  | 'single_to_trio_accepted'
  | 'single_to_trio_declined'
  | 'single_lifetime_795_viewed'
  | 'single_lifetime_795_accepted'
  | 'single_lifetime_795_declined'
  | 'single_lifetime_495_viewed'
  | 'single_lifetime_495_accepted'
  | 'single_lifetime_495_declined'
  | 'three_x1_lifetime_9990_viewed'
  | 'three_x1_lifetime_9990_accepted'
  | 'three_x1_lifetime_9990_declined'
  | 'three_x1_lifetime_8490_viewed'
  | 'three_x1_lifetime_8490_accepted'
  | 'three_x1_lifetime_8490_declined'
  | 'three_x1_lifetime_7990_viewed'
  | 'three_x1_lifetime_7990_accepted'
  | 'three_x1_lifetime_7990_declined'
  | 'single_free_shipping_retention_viewed'
  | 'single_free_shipping_retention_accepted'
  | 'single_free_shipping_retention_declined'
  | 'single_lifetime_shipping_rescue_viewed'
  | 'single_lifetime_shipping_rescue_accepted'
  | 'single_lifetime_shipping_rescue_declined'
  // Generic funnel events: every offer fires these with { offer_id, flow, source }.
  | 'upsell_offer_viewed'
  | 'upsell_offer_accepted'
  | 'upsell_offer_declined'
  | 'upsell_flow_completed'
  | 'upsell_confirmation_viewed'
  | 'crew_pricing_link_clicked'
  | 'want_free_shipping_clicked'
  | 'product_selected'
  | 'quantity_increased'
  | 'quantity_decreased'
  | 'lifetime_selected'
  | 'lifetime_declined'
  | 'pack_switch_suggested'
  | 'pack_switch_accepted'
  | 'objection_selected'
  | 'objection_just_looking_selected'
  | 'mini_funnel_viewed'
  | 'pack_recommendation_selected'
  | 'authenticity_page_visited'
  | 'batch_verification_attempted'
  | 'batch_verification_successful'
  | 'referral_page_visited'
  | 'referral_link_clicked'
  | 'activation_page_visited'
  | 'activation_started'
  | 'activation_completed'
  | 'activation_not_connected'
  // Build Your X1 configurator
  | 'build_your_x1_view'
  | 'essential_started'
  | 'primary_lens_clear'
  | 'primary_lens_amber'
  | 'primary_lens_smoke'
  | 'full_lens_added'
  | 'full_lens_removed'
  | 'hard_case_added'
  | 'hard_case_removed'
  | 'care_kit_added'
  | 'care_kit_removed'
  | 'rx_added'
  | 'rx_quantity'
  | 'one_year_protection_selected'
  | 'lifetime_protection_selected'
  | 'basic_protection_selected'
  | 'complete_offer_shown'
  | 'complete_offer_declined'
  | 'reviews_strip_clicked'
  | 'owner_center_visited'
  | 'quick_start_manual_opened'
  | 'owner_form_opened'
  | 'owner_form_started'
  | 'owner_form_submitted'
  | 'complete_offer_accepted'
  | 'complete_offer_reverted'
  | 'hard_case_downsell_shown'
  | 'hard_case_downsell_accepted'
  | 'hard_case_downsell_declined'
  | 'checkout_started'
  | 'builder_not_connected'
  | 'multipack_rx_quantity'
  | 'hero_offer_selected'
  | 'quick_start_step_viewed'
  | 'quick_start_page_visited'
  | 'track_order_page_visited'
  | 'track_order_submitted'
  | 'rx_section_clicked'
  | 'faq_opened'
  | 'nav_cta_clicked'
  | 'review_submitted'
  | 'discount_code_copied'

/** Product choices that also count as Meta's standard CustomizeProduct event. */
const CUSTOMIZE_EVENTS: ReadonlySet<CustomEventName> = new Set<CustomEventName>([
  'primary_lens_clear',
  'primary_lens_amber',
  'primary_lens_smoke',
  'full_lens_added',
  'hard_case_added',
  'care_kit_added',
  'rx_added',
  'multipack_rx_quantity',
  'one_year_protection_selected',
  'lifetime_protection_selected',
  'lifetime_selected',
  'quantity_increased',
  'pack_switch_accepted',
  'complete_offer_accepted',
  'hard_case_downsell_accepted',
  'bundle_1_selected',
  'bundle_3_selected',
  'bundle_5_selected',
  'hero_offer_selected',
  'pack_recommendation_selected',
])

const tiktokNames: Record<EventName, string> = {
  PageView: 'Pageview',
  ViewContent: 'ViewContent',
  AddToCart: 'AddToCart',
  InitiateCheckout: 'InitiateCheckout',
  Lead: 'SubmitForm',
  CustomizeProduct: 'ClickButton',
  CompleteRegistration: 'CompleteRegistration',
  Contact: 'Contact',
}

interface Win extends Window {
  fbq?: (...args: unknown[]) => void
  ttq?: {
    track: (name: string, data?: unknown, options?: unknown) => void
    page: () => void
    load: (id: string) => void
  }
  dataLayer?: unknown[]
  _fbq?: unknown
}

// ─── Identity: fbp / fbc cookies, visitor id, advanced matching ──────────────────────────

const ATTR_KEY = 'gv_attribution'
const VISITOR_KEY = 'gv_visitor_id'
const ATTR_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'fbclid'] as const

interface UserData {
  em?: string
  ph?: string
  fn?: string
  ln?: string
}
let userData: UserData = {}

function getCookie(name: string): string {
  if (typeof document === 'undefined') return ''
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : ''
}

/** Root domain (gritvuegear.com) so the shop subdomain reads the same cookie. */
function rootDomain(): string {
  const host = window.location.hostname
  if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return ''
  const parts = host.split('.')
  return parts.length > 2 ? parts.slice(-2).join('.') : host
}

function setRootCookie(name: string, value: string, days: number) {
  const domain = rootDomain()
  const expires = new Date(Date.now() + days * 86_400_000).toUTCString()
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/${domain ? `; domain=.${domain}` : ''}; SameSite=Lax`
}

function visitorId(): string {
  try {
    let id = localStorage.getItem(VISITOR_KEY)
    if (!id) {
      id = `gv.${Date.now()}.${Math.random().toString(36).slice(2, 12)}`
      localStorage.setItem(VISITOR_KEY, id)
    }
    return id
  } catch {
    return ''
  }
}

/** Saves the ad's UTM / fbclid for this visit and writes the _fbc click cookie. */
function captureAttribution() {
  const params = new URLSearchParams(window.location.search)
  const found: Record<string, string> = {}
  ATTR_PARAMS.forEach((key) => {
    const value = params.get(key)
    if (value) found[key] = value
  })
  if (Object.keys(found).length > 0) {
    try {
      sessionStorage.setItem(ATTR_KEY, JSON.stringify(found))
    } catch {
      /* storage blocked: attribution simply isn't forwarded */
    }
  }
  const fbclid = found.fbclid
  if (fbclid && !getCookie('_fbc').endsWith(`.${fbclid}`)) {
    setRootCookie('_fbc', `fb.1.${Date.now()}.${fbclid}`, 90)
  }
}

function storedAttribution(): Record<string, string> {
  try {
    return JSON.parse(sessionStorage.getItem(ATTR_KEY) ?? '{}') as Record<string, string>
  } catch {
    return {}
  }
}

/**
 * Adds the ad's UTM parameters and fbclid to a Shopify cart link, so Shopify reports the
 * session under the right source and its own Meta pixel keeps the same click id.
 */
export function withAttribution(href: string): string {
  if (!href || !/^https?:\/\//.test(href)) return href
  const attribution = storedAttribution()
  if (Object.keys(attribution).length === 0) return href
  const [path, query] = href.split('?')
  const params = new URLSearchParams(query ?? '')
  Object.entries(attribution).forEach(([key, value]) => {
    if (!params.has(key)) params.set(key, value)
  })
  return `${path}?${params.toString()}`
}

/**
 * Advanced matching: call with what the customer typed in a form. The browser pixel hashes
 * it itself; the Conversions API copy is SHA-256 hashed here before it leaves the browser.
 */
export function identifyUser(data: { email?: string; phone?: string; name?: string }) {
  const next: UserData = { ...userData }
  if (data.email?.trim()) next.em = data.email.trim().toLowerCase()
  if (data.phone?.trim()) next.ph = data.phone.replace(/\D/g, '')
  if (data.name?.trim()) {
    const [first, ...rest] = data.name.trim().toLowerCase().split(/\s+/)
    next.fn = first
    if (rest.length) next.ln = rest[rest.length - 1]
  }
  userData = next
  const win = window as Win
  if (META_PIXEL_ID && win.fbq) {
    win.fbq('init', META_PIXEL_ID, { ...userData, external_id: visitorId() })
  }
}

async function sha256(value: string): Promise<string> {
  if (!value || !window.crypto?.subtle) return ''
  const bytes = new TextEncoder().encode(value)
  const digest = await window.crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

function newEventId(name: string): string {
  return `${name}.${Date.now()}.${Math.random().toString(36).slice(2, 10)}`
}

/** Server copy of the event for the Conversions API (same event_id, so Meta de-duplicates). */
async function sendToCapi(name: string, eventId: string, customData?: Record<string, unknown>) {
  if (!META_CAPI_ENDPOINT) return
  try {
    const [em, ph, fn, ln, externalId] = await Promise.all([
      sha256(userData.em ?? ''),
      sha256(userData.ph ?? ''),
      sha256(userData.fn ?? ''),
      sha256(userData.ln ?? ''),
      sha256(visitorId()),
    ])
    const user_data: Record<string, unknown> = {
      client_user_agent: navigator.userAgent,
      fbp: getCookie('_fbp') || undefined,
      fbc: getCookie('_fbc') || undefined,
      external_id: externalId ? [externalId] : undefined,
      em: em ? [em] : undefined,
      ph: ph ? [ph] : undefined,
      fn: fn ? [fn] : undefined,
      ln: ln ? [ln] : undefined,
    }
    await fetch(META_CAPI_ENDPOINT, {
      method: 'POST',
      keepalive: true, // survives the redirect to Shopify
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pixel_id: META_PIXEL_ID,
        data: [
          {
            event_name: name,
            event_id: eventId,
            event_time: Math.floor(Date.now() / 1000),
            event_source_url: window.location.href,
            action_source: 'website',
            user_data,
            custom_data: customData ?? {},
          },
        ],
      }),
    })
  } catch {
    /* never block the customer because of analytics */
  }
}

// ─── Events ──────────────────────────────────────────────────────────────────────────────

export function trackEvent(name: EventName, data?: Record<string, unknown>) {
  if (typeof window === 'undefined') return
  const win = window as Win
  const eventId = newEventId(name)
  if (win.fbq) win.fbq('track', name, data ?? {}, { eventID: eventId })
  if (win.ttq) {
    if (name === 'PageView') win.ttq.page()
    else win.ttq.track(tiktokNames[name], data, { event_id: eventId })
  }
  if (win.dataLayer) win.dataLayer.push({ event: name, event_id: eventId, ...data })
  void sendToCapi(name, eventId, data)
}

/**
 * Fires a named funnel event on every analytics destination that is present.
 * Product choices also fire CustomizeProduct; form completions fire their standard event.
 * Safe to call when no pixel is configured: it simply does nothing.
 */
export function trackCustom(name: CustomEventName, data?: Record<string, unknown>) {
  if (typeof window === 'undefined') return
  const win = window as Win
  const eventId = newEventId(name)
  if (win.fbq) win.fbq('trackCustom', name, data ?? {}, { eventID: eventId })
  if (win.ttq) win.ttq.track(name, data, { event_id: eventId })
  if (win.dataLayer) win.dataLayer.push({ event: name, event_id: eventId, ...data })
  void sendToCapi(name, eventId, data)

  if (CUSTOMIZE_EVENTS.has(name)) {
    trackEvent('CustomizeProduct', { content_name: 'GritVue X1', selection: name, currency: 'USD', ...data })
  }
  if (name === 'activation_completed') {
    trackEvent('CompleteRegistration', { content_name: 'GritVue X1 activation', status: 'registered' })
  }
  if (name === 'owner_form_submitted') {
    if (data?.form === 'rewards') trackEvent('Lead', { content_name: 'GritVue Crew Rewards' })
    if (data?.form === 'support') trackEvent('Contact', { content_name: 'GritVue support' })
  }
}

/** Per-pack analytics names, so bundle mix and AOV are measurable by offer. */
export const packEvents: Record<string, { selected: CustomEventName; addToCart: CustomEventName }> = {
  single: { selected: 'bundle_1_selected', addToCart: 'add_to_cart_single' },
  trio: { selected: 'bundle_3_selected', addToCart: 'add_to_cart_full_shift' },
  crew: { selected: 'bundle_5_selected', addToCart: 'add_to_cart_max_value' },
  business: { selected: 'business_pack_clicked', addToCart: 'add_to_cart_business' },
}

/**
 * Fires AddToCart on the click that sends the visitor to the Shopify cart.
 * The browser leaves the page right away, which can cancel the pixel request,
 * so the navigation waits a moment (max ~350ms) for the event to go out.
 */
export function trackShopifyAddToCart(
  event: React.MouseEvent<HTMLAnchorElement>,
  data: { content_name: string; content_ids: string[]; quantity: number; value: number },
) {
  const addons = applyCartAddons(event.currentTarget.href)
  trackEvent('AddToCart', {
    ...data,
    value: Number((data.value + addons.extraValue).toFixed(2)),
    num_items: data.quantity + addons.rx + addons.care,
    rx_quantity: addons.rx,
    care_kit_quantity: addons.care,
    content_type: 'product',
    currency: 'USD',
  })
  const href = withAttribution(addons.href)
  // Let new-tab / modified clicks behave normally, with the add-ons already in the link.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
    event.currentTarget.href = href
    return
  }
  event.preventDefault()
  window.setTimeout(() => {
    window.location.href = href
  }, 350)
}

/** Same as trackShopifyAddToCart, for routing that happens outside a link click (e.g. a popup button). */
export function goToShopifyCart(
  href: string,
  data: { content_name: string; content_ids: string[]; quantity: number; value: number },
) {
  const addons = applyCartAddons(href)
  trackEvent('AddToCart', {
    ...data,
    value: Number((data.value + addons.extraValue).toFixed(2)),
    num_items: data.quantity + addons.rx + addons.care,
    rx_quantity: addons.rx,
    care_kit_quantity: addons.care,
    content_type: 'product',
    currency: 'USD',
  })
  const target = withAttribution(addons.href)
  window.setTimeout(() => {
    window.location.href = target
  }, 350)
}

// ─── Loaders ─────────────────────────────────────────────────────────────────────────────

function injectMeta(id: string) {
  if (document.getElementById('meta-pixel')) return
  const script = document.createElement('script')
  script.id = 'meta-pixel'
  const matching = JSON.stringify({ external_id: visitorId() })
  script.innerHTML = `
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
    document,'script','https://connect.facebook.net/en_US/fbevents.js');
    fbq('init','${id}',${matching});
  `
  document.head.appendChild(script)
}

function injectTikTok(id: string) {
  if (document.getElementById('tiktok-pixel')) return
  const script = document.createElement('script')
  script.id = 'tiktok-pixel'
  script.innerHTML = `
    !function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];
    ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
    ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
    for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
    ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
    ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=r;ttq._t=ttq._t||{};ttq._t[e]=+new Date;
    ttq._o=ttq._o||{};ttq._o[e]=n||{};var o=d.createElement("script");o.type="text/javascript";
    o.async=!0;o.src=r+"?sdkid="+e+"&lib="+t;var a=d.getElementsByTagName("script")[0];
    a.parentNode.insertBefore(o,a)};
    ttq.load('${id}');}(window,document,'ttq');
  `
  document.head.appendChild(script)
}

let initialized = false

/** Loads the pixels once and fires the first PageView (with event_id for de-duplication). */
export function initPixels() {
  if (typeof window === 'undefined' || initialized) return
  initialized = true
  captureAttribution()
  if (META_PIXEL_ID) injectMeta(META_PIXEL_ID)
  if (TIKTOK_PIXEL_ID) injectTikTok(TIKTOK_PIXEL_ID)
  trackEvent('PageView')
}
